import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle, FiCopy, FiRefreshCw, FiLayers
} from 'react-icons/fi';
import { colors, spacing, borderRadius, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, LineSelect, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow,
  SortableTh, SortContext
} from '../../components/SharedStyles';
import { getItemsCached, getVendorsCached, getAccountsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import ItemSelect from '../../components/ItemSelect';
import VendorSelect from '../../components/VendorSelect';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { getItemStockCached } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import { getLinkedAccount } from '../../utils/coaInitializer';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';

const TRAN_TYPE_CODE = 'BOM';

const EMPTY_LINE = {
  componentItemId: '', description: '', quantity: 1, uom: '', unitCode: '',
  scrapPercent: 0, isCritical: false,
  unitCost: 0, lineCost: 0, hasSubBom: false, subBomId: '', subBomCostPerUnit: 0
};

const EMPTY_OVERHEAD = {
  expenseType: 'Labour', accountId: '', accountCode: '', accountName: '',
  amount: 0, allocationMethod: 'Fixed', description: ''
};

const EMPTY_OUTSOURCING = {
  vendorId: '', vendorName: '', accountId: '', accountCode: '', accountName: '',
  description: '', costMethod: 'Per Unit', amount: 0
};

const EXPENSE_TYPES = ['Labour', 'Factory Overhead', 'Utilities', 'Depreciation', 'Other'];
const ALLOCATION_METHODS = ['Per Unit', 'Fixed', 'Percentage of Material'];
const COST_METHODS = ['Per Unit', 'Fixed', 'Per Batch'];
const PRODUCTION_TYPES = ['In-House', 'Outsourced', 'Partial Outsource'];

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Active: colors.success.main,
  Obsolete: colors.neutral[500],
  Cancelled: colors.error.main,
};

const BillOfMaterials = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('store', 'billOfMaterials');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  // View & list state
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docNumber', 'desc');
  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    docNumber: '', bomName: '', finishedItemId: '', outputQuantity: 1, outputUom: '',
    version: 1, effectiveFrom: new Date().toISOString().split('T')[0], effectiveTo: '',
    notes: '', status: 'Draft',
    locationId: '', locationName: '',
    productionType: 'In-House'
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [overheadItems, setOverheadItems] = useState([]);
  const [outsourcingItems, setOutsourcingItems] = useState([]);

  // Reference data
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stockBalances, setStockBalances] = useState({});
  const [activeBoms, setActiveBoms] = useState([]);
  const [printConfig, setPrintConfig] = useState(null);
  const [refreshingCosts, setRefreshingCosts] = useState(false);

  // ======================== DATA LOADERS ========================

  const loadRecords = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const data = await getTransactions(activeCompany.id, {
        tranTypeCode: TRAN_TYPE_CODE,
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      setRecords(data);
    } catch (error) { console.error('Error loading records:', error); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows
        .filter(i => i.isDeleted !== true)
        .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: vendors come from the device-local master cache (0 Firestore reads/open).
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) { console.error('Error loading vendors:', error); }
  }, [activeCompany]);

  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: accounts come from the device-local master cache (0 Firestore reads/open).
      const allAccts = await getAccountsCached(activeCompany.id);
      const allLvl5 = allAccts.filter(a => a.level === 5 || a.isPostable);
      setAccounts(allLvl5
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (error) { console.error('Error loading accounts:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  const loadActiveBoms = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const data = await getTransactions(activeCompany.id, {
        tranTypeCode: TRAN_TYPE_CODE, status: 'Active'
      });
      setActiveBoms(data);
    } catch (error) { console.error('Error loading active BOMs:', error); }
  }, [activeCompany]);

  useEffect(() => {
    loadRecords(); loadItems(); loadLocations(); loadVendors();
    loadAccounts(); loadActiveBoms();
    loadDocumentFormats(activeCompany?.id).then(setPrintConfig);
  }, [loadRecords, loadItems, loadLocations, loadVendors, loadAccounts, loadActiveBoms]);

  // ======================== COMPUTED VALUES ========================

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.bomName?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? (item.itemCode ? item.itemCode + ' - ' : '') + item.itemName : '-';
  };

  // Find sub-BOM for a component item
  const findSubBom = useCallback((componentItemId) => {
    return activeBoms.find(b => b.finishedItemId === componentItemId) || null;
  }, [activeBoms]);

  // Get item cost (avgCost > purchasePrice > 0)
  const getItemCost = useCallback((itemId) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return 0;
    return item.avgCost || item.purchasePrice || 0;
  }, [items]);

  // Calculate costs
  const costSummary = useMemo(() => {
    const materialCost = lineItems.reduce((sum, l) => sum + (l.lineCost || 0), 0);

    const overheadCost = overheadItems.reduce((sum, o) => {
      if (o.allocationMethod === 'Percentage of Material') {
        return sum + (materialCost * (o.amount || 0) / 100);
      }
      return sum + (o.amount || 0);
    }, 0);

    const outsourcingCost = outsourcingItems.reduce((sum, o) => {
      if (o.costMethod === 'Per Unit') {
        return sum + ((o.amount || 0) * (formData.outputQuantity || 1));
      }
      return sum + (o.amount || 0);
    }, 0);

    const totalCost = materialCost + overheadCost + outsourcingCost;
    const costPerUnit = formData.outputQuantity > 0 ? totalCost / formData.outputQuantity : 0;

    return { materialCost, overheadCost, outsourcingCost, totalCost, costPerUnit };
  }, [lineItems, overheadItems, outsourcingItems, formData.outputQuantity]);

  // ======================== FORM HANDLERS ========================

  const handleHeaderChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'finishedItemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated.outputUom = item.unitOfMeasure || '';
        }
      }
      return updated;
    });
  };

  const calculateLineCost = (line) => {
    const qty = Number(line.quantity) || 0;
    const scrap = Number(line.scrapPercent) || 0;
    const effectiveQty = qty * (1 + scrap / 100);
    const cost = Number(line.unitCost) || 0;
    return { ...line, lineCost: effectiveQty * cost };
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'componentItemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index].uom = seeded.unitCode || item.unitOfMeasure || '';
          updated[index].unitCode = seeded.unitCode || '';
          // Auto-pick cost
          const subBom = findSubBom(value);
          if (subBom && subBom.costPerUnit > 0) {
            updated[index].unitCost = subBom.costPerUnit;
            updated[index].hasSubBom = true;
            updated[index].subBomId = subBom.id;
            updated[index].subBomCostPerUnit = subBom.costPerUnit;
          } else {
            updated[index].unitCost = seeded.unitCost || item.avgCost || item.purchasePrice || 0;
            updated[index].hasSubBom = false;
            updated[index].subBomId = '';
            updated[index].subBomCostPerUnit = 0;
          }
        }
      }
      if (field === 'unitCode' && updated[index].componentItemId) {
        const item = items.find(i => i.id === updated[index].componentItemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index].uom = patch.unitCode;
          updated[index].unitCode = patch.unitCode;
          if (!updated[index].hasSubBom) updated[index].unitCost = patch.unitCost;
        }
      }
      updated[index] = calculateLineCost(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.componentItemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Overhead handlers
  const handleOverheadChange = (index, field, value) => {
    setOverheadItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'accountId' && value) {
        const acc = accounts.find(a => a.id === value);
        if (acc) {
          updated[index].accountCode = acc.code || '';
          updated[index].accountName = acc.name || '';
        }
      }
      return updated;
    });
  };
  const addOverhead = () => setOverheadItems(prev => [...prev, { ...EMPTY_OVERHEAD }]);
  const removeOverhead = (index) => setOverheadItems(prev => prev.filter((_, i) => i !== index));

  // Outsourcing handlers
  const handleOutsourcingChange = async (index, field, value) => {
    setOutsourcingItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'vendorId' && value) {
        const vendor = vendors.find(v => v.id === value);
        if (vendor) updated[index].vendorName = vendor.vendorName || '';
      }
      return updated;
    });
    // Auto-resolve vendor's linked payable account
    if (field === 'vendorId' && value && activeCompany?.id) {
      try {
        const linkedAccount = await getLinkedAccount(activeCompany.id, 'vendor', value);
        if (linkedAccount) {
          setOutsourcingItems(prev => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              accountId: linkedAccount.id || '',
              accountCode: linkedAccount.code || '',
              accountName: linkedAccount.name || ''
            };
            return updated;
          });
        }
      } catch (err) { console.error('Error resolving vendor account:', err); }
    }
  };
  const addOutsourcing = () => setOutsourcingItems(prev => [...prev, { ...EMPTY_OUTSOURCING }]);
  const removeOutsourcing = (index) => setOutsourcingItems(prev => prev.filter((_, i) => i !== index));

  // Refresh all component costs from current item data / sub-BOMs
  const handleRefreshCosts = async () => {
    setRefreshingCosts(true);
    try {
      // Reload active BOMs + items for latest costs
      await Promise.all([loadItems(), loadActiveBoms()]);
      // Short delay to let state settle
      setTimeout(() => {
        setLineItems(prev => prev.map(line => {
          if (!line.componentItemId) return line;
          const item = items.find(i => i.id === line.componentItemId);
          const subBom = activeBoms.find(b => b.finishedItemId === line.componentItemId);
          let unitCost = line.unitCost;
          let hasSubBom = false;
          let subBomId = '';
          let subBomCostPerUnit = 0;

          if (subBom && subBom.costPerUnit > 0) {
            unitCost = subBom.costPerUnit;
            hasSubBom = true;
            subBomId = subBom.id;
            subBomCostPerUnit = subBom.costPerUnit;
          } else if (item) {
            unitCost = item.avgCost || item.purchasePrice || 0;
          }
          return calculateLineCost({ ...line, unitCost, hasSubBom, subBomId, subBomCostPerUnit });
        }));
        setRefreshingCosts(false);
      }, 300);
    } catch (error) {
      console.error('Error refreshing costs:', error);
      setRefreshingCosts(false);
    }
  };

  const resetForm = () => {
    setFormData({
      docNumber: '', bomName: '', finishedItemId: '', outputQuantity: 1, outputUom: '',
      version: 1, effectiveFrom: new Date().toISOString().split('T')[0], effectiveTo: '',
      notes: '', status: 'Draft', locationId: '', locationName: '', productionType: 'In-House'
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setOverheadItems([]);
    setOutsourcingItems([]);
    setEditingRecord(null);
  };

  // ======================== ACTIONS ========================

  const handleCreate = () => { resetForm(); setViewMode('form'); };

  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({
      docNumber: rec.docNumber || '', bomName: rec.bomName || '',
      finishedItemId: rec.finishedItemId || '', outputQuantity: rec.outputQuantity || 1,
      outputUom: rec.outputUom || '', version: rec.version || 1,
      effectiveFrom: rec.effectiveFrom || new Date().toISOString().split('T')[0],
      effectiveTo: rec.effectiveTo || '', notes: rec.notes || '',
      status: rec.status || 'Draft', locationId: rec.locationId || '',
      locationName: rec.locationName || '', productionType: rec.productionType || 'In-House'
    });
    setLineItems(rec.lines?.length > 0
      ? rec.lines.map(l => calculateLineCost({ ...EMPTY_LINE, ...l }))
      : [{ ...EMPTY_LINE }]);
    setOverheadItems(rec.overheadExpenses?.length > 0
      ? rec.overheadExpenses.map(o => ({ ...EMPTY_OVERHEAD, ...o }))
      : []);
    setOutsourcingItems(rec.outsourcingItems?.length > 0
      ? rec.outsourcingItems.map(o => ({ ...EMPTY_OUTSOURCING, ...o }))
      : []);
    setViewMode('form');
  };

  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  const handleCopyBom = (rec) => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      bomName: `${rec.bomName} (Copy)`,
      finishedItemId: rec.finishedItemId,
      outputQuantity: rec.outputQuantity || 1,
      outputUom: rec.outputUom || '',
      version: (rec.version || 1) + 1,
      locationId: rec.locationId || '',
      locationName: rec.locationName || '',
      productionType: rec.productionType || 'In-House',
      status: 'Draft'
    }));
    setLineItems(rec.lines?.length > 0
      ? rec.lines.map(l => calculateLineCost({ ...EMPTY_LINE, ...l }))
      : [{ ...EMPTY_LINE }]);
    setOverheadItems(rec.overheadExpenses?.length > 0
      ? rec.overheadExpenses.map(o => ({ ...EMPTY_OVERHEAD, ...o }))
      : []);
    setOutsourcingItems(rec.outsourcingItems?.length > 0
      ? rec.outsourcingItems.map(o => ({ ...EMPTY_OUTSOURCING, ...o }))
      : []);
    setEditingRecord(null);
    setViewMode('form');
  };

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.bomName.trim()) { alert('Please enter a BOM name'); return; }
    if (!formData.finishedItemId) { alert('Please select a finished item'); return; }
    const validLines = lineItems.filter(l => l.componentItemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one component with quantity'); return; }
    if (validLines.some(l => l.componentItemId === formData.finishedItemId)) {
      alert('A component cannot be the same as the finished item (circular reference)'); return;
    }
    if (!activeFinancialYear?.id) {
      alert('No active financial year. Please set up a financial year first.'); return;
    }

    // Financial year date check
    const effectiveFromDate = new Date(formData.effectiveFrom);
    const fyStart = activeFinancialYear.startDate?.toDate ? activeFinancialYear.startDate.toDate() : new Date(activeFinancialYear.startDate);
    const fyEnd = activeFinancialYear.endDate?.toDate ? activeFinancialYear.endDate.toDate() : new Date(activeFinancialYear.endDate);
    if (effectiveFromDate < fyStart || effectiveFromDate > fyEnd) {
      alert(`Effective date must be within the active financial year (${fyStart.toLocaleDateString()} - ${fyEnd.toLocaleDateString()})`);
      return;
    }

    // Validate overhead accounts
    const validOverheads = overheadItems.filter(o => o.amount > 0);
    for (const oh of validOverheads) {
      if (!oh.accountId) {
        alert(`Please select an account for overhead expense: ${oh.expenseType}`); return;
      }
    }

    // Validate outsourcing
    const validOutsourcing = outsourcingItems.filter(o => o.amount > 0);
    for (const os of validOutsourcing) {
      if (!os.vendorId) { alert('Please select a vendor for outsourcing entry'); return; }
      if (!os.accountId) { alert('Please select an account for outsourcing entry'); return; }
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.componentItemId);
        const computed = calculateLineCost(l);
        return {
          ...computed, lineNumber: i + 1,
          itemId: l.componentItemId,
          itemCode: item?.itemCode || '',
          itemName: item?.itemName || l.description || ''
        };
      });

      const status = saveStatus || 'Draft';
      const { materialCost, overheadCost, outsourcingCost, totalCost, costPerUnit } = costSummary;

      const data = {
        bomName: formData.bomName,
        finishedItemId: formData.finishedItemId,
        outputQuantity: formData.outputQuantity,
        outputUom: formData.outputUom,
        version: formData.version,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo,
        docDate: formData.effectiveFrom,
        locationId: formData.locationId,
        locationName: formData.locationName,
        productionType: formData.productionType,
        notes: formData.notes,
        status,
        lines: calculatedLines,
        overheadExpenses: validOverheads,
        outsourcingItems: validOutsourcing,
        materialCost, overheadCost, outsourcingCost, totalCost, costPerUnit,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null
      };

      let transactionId;
      let newTranData = null;

      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        transactionId = editingRecord.id;
      } else {
        newTranData = await createTransaction(activeCompany.id, { ...data, tranTypeCode: TRAN_TYPE_CODE }, currentUser.uid);
        transactionId = newTranData.id;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: transactionId, status }
        : { ...newTranData, status };
      setRecords(prev => editingRecord
        ? prev.map(r => r.id === transactionId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      Promise.all([loadRecords(), loadActiveBoms()]).catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    // The BOM keeps only the finished item's id — print its code and name
    // (as on screen), else the "Finished Item" line prints blank.
    const fin = data.finishedItemId ? getItemName(data.finishedItemId) : '';
    printDocument('billOfMaterials', {
      ...data,
      finishedItemName: data.finishedItemName || (fin !== '-' ? fin : ''),
    }, activeCompany, printConfig, activeBranch);
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (rec.status === 'Cancelled' || rec.status === 'Obsolete') { alert('Cannot cancel this record'); return; }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  // ======================== LIST VIEW ========================

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Active">Active</option>
            <option value="Obsolete">Obsolete</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New BOM</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Bill of Materials Records</h3>
          <p>{records.length === 0 ? 'Create your first BOM' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create BOM</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="bomName">BOM Name</SortableTh><SortableTh field="finishedItemName">Finished Item</SortableTh>
          <SortableTh field="productionType">Type</SortableTh><SortableTh field="version">V</SortableTh>
          <SortableTh field="lines" style={{ textAlign: 'center' }}>Components</SortableTh>
          <SortableTh field="costPerUnit" style={{ textAlign: 'right' }}>Cost/Unit</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.bomName}</Td>
              <Td>{getItemName(rec.finishedItemId)}</Td>
              <Td><TypeBadge $type={rec.productionType}>{rec.productionType || 'In-House'}</TypeBadge></Td>
              <Td>{rec.version || 1}</Td>
              <Td style={{ textAlign: 'center' }}>{rec.lines?.length || 0}</Td>
              <Td style={{ textAlign: 'right' }}>{formatAmount(rec.costPerUnit || 0)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                {canEdit && <IconBtn title="Copy BOM" onClick={() => handleCopyBom(rec)}><FiCopy /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && rec.status !== 'Cancelled' && rec.status !== 'Obsolete' && (
                  <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}>
                    <FiXCircle />
                  </IconBtn>
                )}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <SummaryBar>
        <span>Total: <strong>{filteredRecords.length}</strong> record(s)</span>
        <span>Draft: <strong>{records.filter(r => r.status === 'Draft').length}</strong> | Active: <strong>{records.filter(r => r.status === 'Active').length}</strong> | Obsolete: <strong>{records.filter(r => r.status === 'Obsolete').length}</strong></span>
      </SummaryBar>
    </>
  );

  // ======================== VIEW MODE ========================

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    const matCost = rec.materialCost || 0;
    const ohCost = rec.overheadCost || 0;
    const osCost = rec.outsourcingCost || 0;
    const totCost = rec.totalCost || 0;
    const cpu = rec.costPerUnit || 0;

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber} - {rec.bomName}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            {canEdit && <SaveButton $secondary onClick={() => handleCopyBom(rec)}><FiCopy /> Copy</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>BOM Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>BOM Name</label><span>{rec.bomName}</span></InfoItem>
            <InfoItem><label>Finished Item</label><span>{getItemName(rec.finishedItemId)}</span></InfoItem>
            <InfoItem><label>Output Qty</label><span>{rec.outputQuantity} {rec.outputUom || ''}</span></InfoItem>
            <InfoItem><label>Production Type</label><span>{rec.productionType || 'In-House'}</span></InfoItem>
            <InfoItem><label>Version</label><span>{rec.version || 1}</span></InfoItem>
            <InfoItem><label>Effective From</label><span>{rec.effectiveFrom || '-'}</span></InfoItem>
            <InfoItem><label>Effective To</label><span>{rec.effectiveTo || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{rec.locationName || '-'}</span></InfoItem>
            <InfoItem><label>Status</label><span>{rec.status}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        {/* Components */}
        <FormCard>
          <SectionTitle>Components</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Component</Th><Th style={{ textAlign: 'right' }}>Qty</Th>
            <Th>UOM</Th><Th style={{ textAlign: 'right' }}>Scrap%</Th>
            <Th style={{ textAlign: 'center' }}>Critical</Th>
            <Th style={{ textAlign: 'right' }}>Unit Cost</Th>
            <Th style={{ textAlign: 'right' }}>Line Cost</Th>
            <Th style={{ textAlign: 'center' }}>Sub-BOM</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}>
                <Td>{i + 1}</Td>
                <Td>{line.itemCode ? `${line.itemCode} - ` : ''}{line.description || line.itemName || '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{line.quantity}</Td>
                <Td>{line.uom || '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{line.scrapPercent || 0}%</Td>
                <Td style={{ textAlign: 'center' }}>{line.isCritical ? 'Yes' : '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitCost || 0)}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineCost || 0)}</Td>
                <Td style={{ textAlign: 'center' }}>{line.hasSubBom ? <FiLayers title="Has Sub-BOM" color={colors.primary.main} /> : '-'}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
        </FormCard>

        {/* Overhead Expenses */}
        {rec.overheadExpenses?.length > 0 && (
          <FormCard>
            <SectionTitle>Overhead Expenses</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>#</Th><Th>Expense Type</Th><Th>Account</Th><Th>Allocation</Th>
              <Th>Description</Th><Th style={{ textAlign: 'right' }}>Amount</Th>
            </tr></thead><tbody>
              {rec.overheadExpenses.map((oh, i) => (
                <Tr key={i}>
                  <Td>{i + 1}</Td><Td>{oh.expenseType}</Td>
                  <Td>{oh.accountCode} - {oh.accountName}</Td>
                  <Td>{oh.allocationMethod}</Td>
                  <Td>{oh.description || '-'}</Td>
                  <Td style={{ textAlign: 'right' }}>
                    {oh.allocationMethod === 'Percentage of Material'
                      ? `${oh.amount}% = ${formatAmount(matCost * oh.amount / 100)}`
                      : formatAmount(oh.amount)}
                  </Td>
                </Tr>
              ))}
            </tbody></Table></TableContainer>
          </FormCard>
        )}

        {/* Outsourced Processing */}
        {rec.outsourcingItems?.length > 0 && (
          <FormCard>
            <SectionTitle>Outsourced Processing</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>#</Th><Th>Vendor</Th><Th>Description</Th>
              <Th>Cost Method</Th><Th style={{ textAlign: 'right' }}>Amount</Th>
            </tr></thead><tbody>
              {rec.outsourcingItems.map((os, i) => (
                <Tr key={i}>
                  <Td>{i + 1}</Td><Td>{os.vendorName}</Td>
                  <Td>{os.description || '-'}</Td><Td>{os.costMethod}</Td>
                  <Td style={{ textAlign: 'right' }}>
                    {os.costMethod === 'Per Unit'
                      ? `${formatAmount(os.amount)} × ${rec.outputQuantity} = ${formatAmount(os.amount * rec.outputQuantity)}`
                      : formatAmount(os.amount)}
                  </Td>
                </Tr>
              ))}
            </tbody></Table></TableContainer>
          </FormCard>
        )}

        {/* Cost Summary */}
        <FormCard>
          <SectionTitle>Cost Summary</SectionTitle>
          <CostSummaryGrid>
            <CostItem>
              <CostLabel>Material Cost</CostLabel>
              <CostValue>{formatAmount(matCost)}</CostValue>
            </CostItem>
            <CostItem>
              <CostLabel>Overhead Cost</CostLabel>
              <CostValue>{formatAmount(ohCost)}</CostValue>
            </CostItem>
            <CostItem>
              <CostLabel>Outsourcing Cost</CostLabel>
              <CostValue>{formatAmount(osCost)}</CostValue>
            </CostItem>
            <CostItem $highlight>
              <CostLabel>Total Cost</CostLabel>
              <CostValue>{formatAmount(totCost)}</CostValue>
            </CostItem>
            <CostItem $primary>
              <CostLabel>Cost Per Unit ({rec.outputQuantity} {rec.outputUom})</CostLabel>
              <CostValue>{formatAmount(cpu)}</CostValue>
            </CostItem>
          </CostSummaryGrid>
        </FormCard>

        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
      </>
    );
  };

  // ======================== FORM VIEW ========================

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Bill of Materials'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && (
            <SaveButton $approve onClick={() => handleSave('Active')} disabled={saving}><FiCheck /> Activate</SaveButton>
          )}
        </FormActions>
      </FormHeader>

      {/* BOM Header */}
      <FormCard>
        <SectionTitle>BOM Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>BOM Name *</Label><Input value={formData.bomName} onChange={e => handleHeaderChange('bomName', e.target.value)} placeholder="Bill of Materials name" /></FormGroup>
          <FormGroup><Label>Finished Item *</Label><ItemSelect items={items} selectedItemId={formData.finishedItemId} onChange={(itemId) => handleHeaderChange('finishedItemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></FormGroup>
          <FormGroup><Label>Output Quantity</Label><Input type="number" min="1" value={formData.outputQuantity} onChange={e => handleHeaderChange('outputQuantity', Number(e.target.value))} /></FormGroup>
          <FormGroup><Label>Output UOM</Label><Input value={formData.outputUom} onChange={e => handleHeaderChange('outputUom', e.target.value)} placeholder="Unit of measure" /></FormGroup>
          <FormGroup>
            <Label>Production Type</Label>
            <Select value={formData.productionType} onChange={e => handleHeaderChange('productionType', e.target.value)}>
              {PRODUCTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Version</Label><Input type="number" min="1" value={formData.version} onChange={e => handleHeaderChange('version', Number(e.target.value))} /></FormGroup>
          <FormGroup><Label>Effective From</Label><Input type="date" value={formData.effectiveFrom} onChange={e => handleHeaderChange('effectiveFrom', e.target.value)} /></FormGroup>
          <FormGroup><Label>Effective To</Label><Input type="date" value={formData.effectiveTo} onChange={e => handleHeaderChange('effectiveTo', e.target.value)} /></FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('locationId', e.target.value);
              handleHeaderChange('locationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.locationName}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
        </FormGrid>
      </FormCard>

      {/* Components Section */}
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Components</SectionTitle>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <RefreshButton onClick={handleRefreshCosts} disabled={refreshingCosts}>
              <FiRefreshCw className={refreshingCosts ? 'spinning' : ''} /> {refreshingCosts ? 'Refreshing...' : 'Refresh Costs'}
            </RefreshButton>
            <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
          </div>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 36, minWidth: 36, maxWidth: 36 }}>#</LineTh>
          <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Component Item</LineTh>
          <LineTh style={{ width: 170, minWidth: 170, maxWidth: 170 }}>Description</LineTh>
          <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Qty</LineTh>
          <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>UOM</LineTh>
          <LineTh style={{ width: 65, minWidth: 65, maxWidth: 65 }}>Scrap%</LineTh>
          <LineTh style={{ width: 50, textAlign: 'center' }}>Crit</LineTh>
          <LineTh style={{ width: 90, textAlign: 'right' }}>Unit Cost</LineTh>
          <LineTh style={{ width: 90, textAlign: 'right' }}>Line Cost</LineTh>
          <LineTh style={{ width: 36, minWidth: 36, maxWidth: 36 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => (
            <tr key={index}>
              <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>
                {index + 1}
                {line.hasSubBom && <FiLayers size={12} title="Has Sub-BOM" color={colors.primary.main} style={{ display: 'block', margin: '2px auto 0' }} />}
              </LineTd>
              <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.componentItemId} onChange={(itemId) => handleLineChange(index, 'componentItemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
              <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd>{(() => {
                const itm = items.find(i => i.id === line.componentItemId);
                const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                if (itmUnits.length > 1) {
                  return <LineUnitPicker item={itm} line={{ ...line, unitOfMeasure: line.uom }} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} />;
                }
                return <LineInput value={line.uom} onChange={e => handleLineChange(index, 'uom', e.target.value)} placeholder="UOM" />;
              })()}</LineTd>
              <LineTd><LineInput type="number" min="0" max="100" step="0.1" value={line.scrapPercent} onChange={e => handleLineChange(index, 'scrapPercent', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd style={{ textAlign: 'center' }}><CheckboxInput type="checkbox" checked={line.isCritical} onChange={e => handleLineChange(index, 'isCritical', e.target.checked)} /></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.unitCost} onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd style={{ textAlign: 'right', fontWeight: 600, color: colors.text.primary }}>{formatAmount(line.lineCost || 0)}</LineTd>
              <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
            </tr>
          ))}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection>
          <TotalsTable><tbody>
            <TotalRow><td>Total Material Cost</td><td>{formatAmount(costSummary.materialCost)}</td></TotalRow>
          </tbody></TotalsTable>
        </TotalsSection>
      </FormCard>

      {/* Overhead Expenses Section */}
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Overhead Expenses</SectionTitle>
          <AddLineButton onClick={addOverhead}><FiPlus /> Add Overhead</AddLineButton>
        </LineItemsHeader>
        {overheadItems.length === 0 ? (
          <EmptySection>No overhead expenses added. Click "Add Overhead" to include labour, factory overhead, etc.</EmptySection>
        ) : (
          <LineTableContainer><LineTable><thead><tr>
            <LineTh style={{ width: 36, minWidth: 36, maxWidth: 36 }}>#</LineTh>
            <LineTh style={{ width: 170, minWidth: 170, maxWidth: 170 }}>Expense Type</LineTh>
            <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Account</LineTh>
            <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Allocation</LineTh>
            <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Description</LineTh>
            <LineTh style={{ width: 100, textAlign: 'right' }}>Amount</LineTh>
            <LineTh style={{ width: 100, textAlign: 'right' }}>Effective</LineTh>
            <LineTh style={{ width: 36, minWidth: 36, maxWidth: 36 }}></LineTh>
          </tr></thead><tbody>
            {overheadItems.map((oh, index) => (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd>
                  <LineSelect value={oh.expenseType} onChange={e => handleOverheadChange(index, 'expenseType', e.target.value)}>
                    {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </LineSelect>
                </LineTd>
                <LineTd>
                  <AccountSelect value={oh.accountId} onChange={e => handleOverheadChange(index, 'accountId', e.target.value)}>
                    <option value="">-- Select Account --</option>
                    {accounts.filter(a => a.code?.startsWith('5') || a.code?.startsWith('6')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </AccountSelect>
                </LineTd>
                <LineTd>
                  <LineSelect value={oh.allocationMethod} onChange={e => handleOverheadChange(index, 'allocationMethod', e.target.value)}>
                    {ALLOCATION_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </LineSelect>
                </LineTd>
                <LineTd><LineInput value={oh.description} onChange={e => handleOverheadChange(index, 'description', e.target.value)} placeholder="Details" /></LineTd>
                <LineTd>
                  <LineInput type="number" min="0" step="0.01" value={oh.amount}
                    onChange={e => handleOverheadChange(index, 'amount', Number(e.target.value))}
                    style={{ textAlign: 'right' }}
                    placeholder={oh.allocationMethod === 'Percentage of Material' ? '%' : 'Amount'}
                  />
                </LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600, color: colors.text.primary }}>
                  {oh.allocationMethod === 'Percentage of Material'
                    ? formatAmount(costSummary.materialCost * (oh.amount || 0) / 100)
                    : formatAmount(oh.amount || 0)}
                </LineTd>
                <LineTd style={{ textAlign: 'center' }}><RemoveLineBtn onClick={() => removeOverhead(index)}><FiX /></RemoveLineBtn></LineTd>
              </tr>
            ))}
          </tbody></LineTable></LineTableContainer>
        )}
        {overheadItems.length > 0 && (
          <TotalsSection>
            <TotalsTable><tbody>
              <TotalRow><td>Total Overhead</td><td>{formatAmount(costSummary.overheadCost)}</td></TotalRow>
            </tbody></TotalsTable>
          </TotalsSection>
        )}
      </FormCard>

      {/* Outsourced Processing Section */}
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Outsourced Processing</SectionTitle>
          <AddLineButton onClick={addOutsourcing}><FiPlus /> Add Process</AddLineButton>
        </LineItemsHeader>
        {outsourcingItems.length === 0 ? (
          <EmptySection>No outsourced processing. Click "Add Process" to include external vendor work.</EmptySection>
        ) : (
          <OverflowVisible><LineTable><thead><tr>
            <LineTh style={{ width: 36, minWidth: 36, maxWidth: 36 }}>#</LineTh>
            <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Vendor</LineTh>
            <LineTh style={{ width: 170, minWidth: 170, maxWidth: 170 }}>Description</LineTh>
            <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Cost Method</LineTh>
            <LineTh style={{ width: 90, textAlign: 'right' }}>Rate/Amt</LineTh>
            <LineTh style={{ width: 100, textAlign: 'right' }}>Effective</LineTh>
            <LineTh style={{ width: 36, minWidth: 36, maxWidth: 36 }}></LineTh>
          </tr></thead><tbody>
            {outsourcingItems.map((os, index) => (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd>
                  <VendorSelect vendors={vendors} selectedVendorId={os.vendorId}
                    onChange={(vendorId) => handleOutsourcingChange(index, 'vendorId', vendorId)} />
                </LineTd>
                <LineTd><LineInput value={os.description} onChange={e => handleOutsourcingChange(index, 'description', e.target.value)} placeholder="Process details" /></LineTd>
                <LineTd>
                  <LineSelect value={os.costMethod} onChange={e => handleOutsourcingChange(index, 'costMethod', e.target.value)}>
                    {COST_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </LineSelect>
                </LineTd>
                <LineTd>
                  <LineInput type="number" min="0" step="0.01" value={os.amount}
                    onChange={e => handleOutsourcingChange(index, 'amount', Number(e.target.value))}
                    style={{ textAlign: 'right' }} />
                </LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600, color: colors.text.primary }}>
                  {os.costMethod === 'Per Unit'
                    ? formatAmount((os.amount || 0) * (formData.outputQuantity || 1))
                    : formatAmount(os.amount || 0)}
                </LineTd>
                <LineTd style={{ textAlign: 'center' }}><RemoveLineBtn onClick={() => removeOutsourcing(index)}><FiX /></RemoveLineBtn></LineTd>
              </tr>
            ))}
          </tbody></LineTable></OverflowVisible>
        )}
        {outsourcingItems.length > 0 && (
          <TotalsSection>
            <TotalsTable><tbody>
              <TotalRow><td>Total Outsourcing</td><td>{formatAmount(costSummary.outsourcingCost)}</td></TotalRow>
            </tbody></TotalsTable>
          </TotalsSection>
        )}
      </FormCard>

      {/* Cost Summary */}
      <FormCard>
        <SectionTitle>Cost Summary</SectionTitle>
        <CostSummaryGrid>
          <CostItem>
            <CostLabel>Material Cost</CostLabel>
            <CostValue>{formatAmount(costSummary.materialCost)}</CostValue>
          </CostItem>
          <CostItem>
            <CostLabel>Overhead Cost</CostLabel>
            <CostValue>{formatAmount(costSummary.overheadCost)}</CostValue>
          </CostItem>
          <CostItem>
            <CostLabel>Outsourcing Cost</CostLabel>
            <CostValue>{formatAmount(costSummary.outsourcingCost)}</CostValue>
          </CostItem>
          <CostItem $highlight>
            <CostLabel>Total Cost</CostLabel>
            <CostValue>{formatAmount(costSummary.totalCost)}</CostValue>
          </CostItem>
          <CostItem $primary>
            <CostLabel>Cost Per Unit ({formData.outputQuantity} {formData.outputUom})</CostLabel>
            <CostValue>{formatAmount(costSummary.costPerUnit)}</CostValue>
          </CostItem>
        </CostSummaryGrid>
      </FormCard>

      {/* Notes */}
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>
    </>
  );

  // ======================== SHORTCUTS & RENDER ========================

  useDocumentShortcuts({ onNew: canEdit ? handleCreate : undefined, onSave: canEdit ? () => handleSave('Active') : undefined, viewMode, saving });

  return (
    <PageLayout pageTitle="Bill of Materials" breadcrumb="Store / Bill of Materials">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ LOCAL STYLED COMPONENTS ============

const CheckboxInput = styled.input`width:18px;height:18px;cursor:pointer;accent-color:${colors.primary.main};`;

const AccountSelect = styled.select`
  width:100%;padding:6px 8px;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
  font-size:${typography.fontSize.xs};color:${colors.text.primary};background:${colors.background.default};
  max-width:220px;
  &:focus{border-color:${colors.primary.main};outline:none;}
`;

const TypeBadge = styled.span`
  display:inline-block;padding:2px 8px;border-radius:10px;font-size:${typography.fontSize.xs};
  font-weight:${typography.fontWeight.medium};
  background:${props => props.$type === 'Outsourced' ? colors.warning.main + '20' : props.$type === 'Partial Outsource' ? colors.info?.main + '20' || '#e3f2fd' : colors.success.main + '20'};
  color:${props => props.$type === 'Outsourced' ? colors.warning.dark || colors.warning.main : props.$type === 'Partial Outsource' ? colors.info?.dark || '#1565c0' : colors.success.dark || colors.success.main};
`;

const CostSummaryGrid = styled.div`
  display:flex;flex-wrap:wrap;gap:${spacing.md};padding:${spacing.md} 0;
  @media(max-width:768px){flex-direction:column;}
`;

const CostItem = styled.div`
  flex:1;min-width:150px;padding:${spacing.md};border-radius:${borderRadius.md};text-align:center;
  background:${props => props.$primary ? colors.primary.main + '10' : props.$highlight ? colors.warning.main + '10' : colors.neutral[50]};
  border:1px solid ${props => props.$primary ? colors.primary.main + '30' : props.$highlight ? colors.warning.main + '30' : colors.border.light};
`;

const CostLabel = styled.div`
  font-size:${typography.fontSize.xs};color:${colors.text.secondary};margin-bottom:4px;
  font-weight:${typography.fontWeight.medium};
`;

const CostValue = styled.div`
  font-size:${typography.fontSize.lg};font-weight:${typography.fontWeight.bold};color:${colors.text.primary};
`;

const EmptySection = styled.div`
  padding:${spacing.lg};text-align:center;color:${colors.text.tertiary};font-size:${typography.fontSize.sm};
  border:1px dashed ${colors.border.light};border-radius:${borderRadius.sm};margin:${spacing.sm} 0;
`;

const RefreshButton = styled.button`
  display:inline-flex;align-items:center;gap:6px;padding:6px 14px;
  border:1px solid ${colors.primary.main};border-radius:${borderRadius.sm};
  background:${colors.background.default};color:${colors.primary.main};
  font-size:${typography.fontSize.sm};cursor:pointer;
  font-weight:${typography.fontWeight.medium};
  &:hover{background:${colors.primary.main}10;}
  &:disabled{opacity:0.6;cursor:not-allowed;}
  .spinning{animation:spin 1s linear infinite;}
  @keyframes spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
`;

const OverflowVisible = styled.div`
  overflow:visible;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
`;

export default BillOfMaterials;
